import type { GccPathway, GccShiftTransformVerdict, GccTaskSequencing, GccWave } from './types'

// Zod-free so the UI can import it without pulling in the schema layer.

export const GCC_WAVES: readonly GccWave[] = [1, 2, 3]

/**
 * The one set of wave time bands (WI 2325), used by the Step 3 prompt, the
 * dashboard, tooltips and the HTML export. Months from the start of the programme.
 */
export const GCC_WAVE_BANDS: Readonly<Record<GccWave, { fromMonths: number; toMonths: number | null; label: string }>> = {
  1: { fromMonths: 0, toMonths: 6, label: '0–6 months' },
  2: { fromMonths: 6, toMonths: 18, label: '6–18 months' },
  3: { fromMonths: 18, toMonths: null, label: '18+ months' },
}

/** Every Step 3 pathway, in tie-break order for the capability headline. */
export const GCC_PATHWAYS: readonly GccPathway[] = [
  'TransformFirst',
  'ShiftFirst',
  'ShiftAndTransform',
  'RetainAndTransform',
  'ShiftOnly',
  'RetainAsIs',
]

export const GCC_PATHWAY_LABELS: Readonly<Record<GccPathway, string>> = {
  TransformFirst: 'Transform First',
  ShiftFirst: 'Shift First',
  ShiftAndTransform: 'Shift & Transform Together',
  RetainAndTransform: 'Retain & Transform',
  ShiftOnly: 'Shift Only',
  RetainAsIs: 'Retain As Is',
}

/** Lowest score (1–5) that lands in each wave: 4–5 → Wave 1, 3 → Wave 2, 1–2 → Wave 3. */
export const GCC_SCORE_WAVE_THRESHOLDS: Readonly<{ wave1: number; wave2: number }> = { wave1: 4, wave2: 3 }

/** Wave of a 1–5 readiness or feasibility score. */
export function scoreToWave(score: number): GccWave {
  if (score >= GCC_SCORE_WAVE_THRESHOLDS.wave1) return 1
  if (score >= GCC_SCORE_WAVE_THRESHOLDS.wave2) return 2
  return 3
}

/** Wave whose time band holds a month count: 0–6 → Wave 1, over 6 to 18 → Wave 2, over 18 → Wave 3 (WI 2806). */
export function monthsToWave(months: number): GccWave {
  return GCC_WAVES.find((w) => {
    const to = GCC_WAVE_BANDS[w].toMonths
    return to === null || months <= to
  }) as GccWave
}

/** v1 Now / Next / Later / Never verdict of a wave, so verdicts and waves share the bands. */
export function waveToVerdict(wave: GccWave | null | undefined): GccShiftTransformVerdict {
  if (wave === 1) return 'Now'
  if (wave === 2) return 'Next'
  if (wave === 3) return 'Later'
  return 'Never'
}

/** "Wave 2 (6–18 months)" */
export function formatWaveBand(wave: GccWave): string {
  return `Wave ${wave} (${GCC_WAVE_BANDS[wave].label})`
}

/**
 * The pathway rule table. A null wave means that lever does not apply.
 *
 * | transformWave | migrationWave | pathway            |
 * |---------------|---------------|--------------------|
 * | null          | null          | RetainAsIs         |
 * | null          | set           | ShiftOnly          |
 * | set           | null          | RetainAndTransform |
 * | t < m         |               | TransformFirst     |
 * | m < t         |               | ShiftFirst         |
 * | t = m         |               | ShiftAndTransform  |
 */
export function derivePathway(transformWave: GccWave | null, migrationWave: GccWave | null): GccPathway {
  if (transformWave === null) return migrationWave === null ? 'RetainAsIs' : 'ShiftOnly'
  if (migrationWave === null) return 'RetainAndTransform'
  if (transformWave < migrationWave) return 'TransformFirst'
  if (migrationWave < transformWave) return 'ShiftFirst'
  return 'ShiftAndTransform'
}

/**
 * Tasks whose waves break the pathway rules. Always empty for results the
 * pipeline calculated; Pipeline Traceability shows it as a check.
 */
export function findWaveViolations(tasks: ReadonlyArray<Pick<GccTaskSequencing, 'taskId' | 'pathway' | 'transformWave' | 'migrationWave'>>): string[] {
  return tasks.filter((t) => derivePathway(t.transformWave, t.migrationWave) !== t.pathway).map((t) => t.taskId)
}
